import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/styles';
import Skeleton from 'react-loading-skeleton';
import countryCodes from 'country-codes-list';
import get from 'lodash.get';
import InfoIcon from '@material-ui/icons/Info';
import Tooltip from '@material-ui/core/Tooltip';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Divider,
  Typography,
  TextField
} from '@material-ui/core';

// library only supports exporting a custom list
const countryCodesObject = countryCodes.customList(
  'countryCode',
  '{countryNameEn}||{countryCallingCode}||{countryCode}'
);

// map custom list into array
const countryCodesList = Object.keys(countryCodesObject)
  .map(key => {
    const [name, callingCode, countryCode] = countryCodesObject[key].split(
      '||'
    );
    return { name, callingCode, countryCode };
  })
  .filter(option => option.callingCode);

const useStyles = makeStyles(theme => ({
  defaultCountryCodeBox: {
    display: 'flex'
  },
  infoIcon: {
    marginLeft: theme.spacing(2),
    marginTop: '12px'
  }
}));

const DEFAULT_SMS_COUNTRY_CODE_PATH = 'sms.defaultSMSCountryCodeInfo';
const DEFAULT_SMS_COUNTRY_CODE_ERROR_PATH =
  DEFAULT_SMS_COUNTRY_CODE_PATH + '.callingCode';

const SmsSettings = props => {
  const { className, useMetaDataSettings, ...rest } = props;
  const classes = useStyles();
  const {
    handleChange,
    contextMetaData,
    tenantMetaData,
    isLoading,
    onBlur,
    onFocus,
    shouldShowError,
    getErrorMessage
  } = useMetaDataSettings;

  const getDisplayName = option => {
    if (!option.callingCode) {
      return '';
    }

    if (option.isDefault) {
      return `${option.name} (+${option.callingCode}) - Default`;
    }

    return `${option.name} (+${option.callingCode})`;
  };

  const onSmsCountryCodeChange = (e, option, reason) => {
    let value;

    if (reason === 'create-option') {
      value = { callingCode: +option, countryCode: null };
    } else if (option === null || option.isDefault) {
      value = null;
    } else {
      value = {
        callingCode: +option.callingCode,
        countryCode: option.countryCode
      };
    }

    handleChange({
      target: {
        name: DEFAULT_SMS_COUNTRY_CODE_PATH,
        value
      }
    });
  };

  let countryCodesListOptions = countryCodesList.slice();

  const getDefaultTimeZoneOption = (
    deviceContextSettings,
    isTenant = false
  ) => {
    const defaultSMSCountryCodeInfo = get(
      deviceContextSettings,
      DEFAULT_SMS_COUNTRY_CODE_PATH,
      null
    );

    if (!defaultSMSCountryCodeInfo) {
      return null;
    }

    const found =
      countryCodesListOptions.find(
        item => item.countryCode === defaultSMSCountryCodeInfo.countryCode
      ) || {};

    const name = found.name || defaultSMSCountryCodeInfo.name || 'Custom';

    const callingCode =
      found.callingCode || defaultSMSCountryCodeInfo.callingCode || 0;

    const countryCode =
      found.countryCode || defaultSMSCountryCodeInfo.countryCode || 'custom';

    return { name, callingCode, countryCode, isDefault: isTenant };
  };

  const contextTimeZone = getDefaultTimeZoneOption(contextMetaData);
  const tenantTimeZone = getDefaultTimeZoneOption(tenantMetaData, true);

  const defaultTimeZone = contextTimeZone || tenantTimeZone || null;

  const SkeletonCardContent = () => {
    return (
      <CardContent>
        <Grid item md={6} xs={12}>
          <Typography display="block" style={{ fontSize: '2.2rem' }}>
            <Skeleton />
          </Typography>
        </Grid>
      </CardContent>
    );
  };

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <CardHeader subheader="Manage SMS settings" title="SMS Settings" />
      <Divider />
      {isLoading ? (
        <SkeletonCardContent />
      ) : (
        <CardContent>
          <Box className={classes.defaultCountryCodeBox}>
            <Autocomplete
              id="default-sms-country-code"
              name={DEFAULT_SMS_COUNTRY_CODE_PATH}
              options={countryCodesListOptions}
              freeSolo
              forcePopupIcon={true}
              getOptionLabel={getDisplayName}
              style={{ width: 300 }}
              onBlur={() =>
                onBlur([
                  DEFAULT_SMS_COUNTRY_CODE_ERROR_PATH,
                  DEFAULT_SMS_COUNTRY_CODE_PATH
                ])
              }
              onFocus={() =>
                onFocus([
                  DEFAULT_SMS_COUNTRY_CODE_ERROR_PATH,
                  DEFAULT_SMS_COUNTRY_CODE_PATH
                ])
              }
              onChange={onSmsCountryCodeChange}
              value={defaultTimeZone}
              renderInput={params => (
                <TextField
                  error={shouldShowError([
                    DEFAULT_SMS_COUNTRY_CODE_ERROR_PATH,
                    DEFAULT_SMS_COUNTRY_CODE_PATH
                  ])}
                  helperText={getErrorMessage([
                    DEFAULT_SMS_COUNTRY_CODE_ERROR_PATH,
                    DEFAULT_SMS_COUNTRY_CODE_PATH
                  ])}
                  {...params}
                  label="Default Country Code"
                  variant="outlined"
                />
              )}
            />

            <Tooltip
              className={classes.infoIcon}
              title="Add a custom contry code by inputing the number and pressing enter">
              <InfoIcon />
            </Tooltip>
          </Box>
        </CardContent>
      )}
      <Divider />
    </Card>
  );
};

SmsSettings.propTypes = {
  className: PropTypes.string,
  useDeviceContextSettings: PropTypes.object
};

export default SmsSettings;
